import { type FC } from "react";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import styled from "styled-components";
import { theme } from "../../../theme";
import Subheading from "../../atoms/typography/Subheading";
import BodySmall from "../../atoms/typography/BodySmall";
import ChevronButton from "../../atoms/buttons/ChevronButtons";
import InvoiceListOverviewItem from "./InvoiceListOverviewItem";
import type { InvoiceProcessStatus } from "../../../models/InvoiceStatusModel";
import type { InvoiceTag } from "../../../models/InvoiceTags";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { InvoiceAssessmentRoute } from "../../../utils/routes";
import TextButton from "../../atoms/buttons/TextButton";
import { formatPrice, getCurrencySymbol } from "../../../utils/formatters";
import { OpenLinkIconSmall } from "../../atoms/icons/OpenLinkIcon";
import type { PriceModel } from "../../../models/ClientsWithInvoicesModel";
import VerticalDivider from "../../atoms/VerticalDivider";

interface Props {
  selectedInvoices: string[];
  setSelectedInvoices: (invoices: string[]) => void;
  clientIdentifier: string;
  clientName: string;
  numberOfInvoices: number;
  filteredTotalAmountByCurrency: PriceModel[];
  statusCounts: Array<{ status: InvoiceProcessStatus; count: number }>;
  invoiceDateFrom?: Date;
  invoiceDateTo?: Date;
  dueDateFrom?: Date;
  dueDateTo?: Date;
  invoiceStatuses?: InvoiceProcessStatus[];
  amountFrom?: number;
  amountTo?: number;
  selectedTags?: InvoiceTag[];
  onInvoicesLoaded?: (
    items: Array<{ id: string; tags?: Array<{ tag: InvoiceTag }> }>,
  ) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  selectedTab?: string;
  unhandledDocumentCount?: number;
}

const ClientOverview = styled.div<{ $isExpanded?: boolean }>`
  padding: 12px 16px;
  background-color: ${({ $isExpanded }) =>
    $isExpanded ? theme.colors.hover : theme.colors.white};
  border-bottom: 2px solid ${theme.colors.grey05};
  display: flex;
  gap: 24px;
  align-items: center;

  &:hover {
    background-color: ${theme.colors.hover};
    cursor: pointer;
  }
`;

const TextFlexContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;
const TextFlexBetweenContainer = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
`;

const OtherSegmentsContainer = styled.div`
  margin-left: auto;
  display: flex;
  gap: 32px;
  align-items: center;
`;

export const ClientOverviewHeaderItem: FC<Props> = ({
  selectedInvoices,
  setSelectedInvoices,
  clientIdentifier,
  clientName,
  numberOfInvoices,
  filteredTotalAmountByCurrency,
  statusCounts,
  invoiceDateFrom,
  invoiceDateTo,
  dueDateFrom,
  dueDateTo,
  invoiceStatuses,
  amountFrom,
  amountTo,
  selectedTags,
  onInvoicesLoaded,
  isExpanded,
  onToggleExpanded,
  selectedTab,
  unhandledDocumentCount,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  //FIXME: Registration does not represent actually in registration
  //const numberInRegistration =
  //  statusCounts.find((status) => status.status === "REGISTRATION")?.count || 0;
  const numberInValidation =
    statusCounts.find((status) => status.status === "VALIDATION")?.count || 0;
  const numberInAssessment =
    statusCounts.find((status) => status.status === "ASSESSMENT")?.count || 0;

  return (
    <div>
      <ClientOverview $isExpanded={isExpanded} onClick={onToggleExpanded}>
        <div>
          <TextFlexContainer>
            <Subheading color={theme.colors.primary}>{clientName}</Subheading>
            <BodySmall color={theme.colors.textSecondary}>
              {numberOfInvoices} st
            </BodySmall>
          </TextFlexContainer>
          <TextFlexBetweenContainer>
            <BodySmall color={theme.colors.textSecondary}>
              {clientIdentifier}
            </BodySmall>
            <BodySmall
              color={theme.colors.textSecondary}
              style={{ display: "flex", gap: 8, alignItems: "center" }}
            >
              {filteredTotalAmountByCurrency.map((price, index) => (
                <span
                  key={price.currency}
                  style={{ display: "flex", gap: 8, alignItems: "center" }}
                >
                  {index > 0 && (
                    <VerticalDivider
                      color={theme.colors.grey20}
                      height="16px"
                    />
                  )}
                  {formatPrice(price.amount)}{" "}
                  {getCurrencySymbol(price.currency)}
                </span>
              ))}
            </BodySmall>
          </TextFlexBetweenContainer>
        </div>

        <OtherSegmentsContainer>
          {selectedTab === "underAssessment" && (
            <TextButton
              title={t("invoiceAssessment.openAssessment")}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/invoices/assess/${clientIdentifier}`, {
                  state: { from: InvoiceAssessmentRoute.id },
                });
              }}
              color={theme.colors.primary}
            />
          )}
          {!!unhandledDocumentCount && (
            <TextButton
              title={t("invoices.unregisteredDocuments", {
                count: unhandledDocumentCount,
              })}
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/client/${clientIdentifier}/invoicesNew/documents?tab=new`,
                );
              }}
            />
          )}
          {selectedTab !== "underValidation" && (
            <TextButton
              title={t("invoices.inValidation", {
                count: numberInValidation,
              })}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/invoices/validate`, {
                  state: {
                    selectedCompanyId: clientIdentifier,
                    selectedCompanyName: clientName,
                  },
                });
              }}
              disabled={numberInValidation === 0}
            />
          )}
          {selectedTab !== "underAssessment" && (
            <TextButton
              title={t("invoices.inAssessment", {
                count: numberInAssessment,
              })}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/invoices/assess`, {
                  state: {
                    selectedCompanyId: clientIdentifier,
                    selectedCompanyName: clientName,
                  },
                });
              }}
              disabled={numberInAssessment === 0}
            />
          )}
          <TextButton
            title={t("clientCard.clientCard")}
            color={theme.colors.primary}
            onClick={(e) => {
              e?.stopPropagation();
              window.open(`/client/${clientIdentifier}`, "_blank");
            }}
            icon={<OpenLinkIconSmall color={theme.colors.primary} />}
            iconPosition="right"
          />
        </OtherSegmentsContainer>

        <div style={{ display: "flex" }} onClick={(e) => e.stopPropagation()}>
          <ChevronButton
            type={isExpanded ? "up" : "down"}
            onClick={onToggleExpanded}
          />
        </div>
      </ClientOverview>
      <SlideOutHeight isVisible={isExpanded}>
        <InvoiceListOverviewItem
          selectedInvoices={selectedInvoices}
          setSelectedInvoices={setSelectedInvoices}
          clientIdentifier={clientIdentifier}
          invoiceDateFrom={invoiceDateFrom}
          invoiceDateTo={invoiceDateTo}
          dueDateFrom={dueDateFrom}
          dueDateTo={dueDateTo}
          invoiceStatuses={invoiceStatuses}
          amountFrom={amountFrom}
          amountTo={amountTo}
          selectedTags={selectedTags}
          onInvoicesLoaded={onInvoicesLoaded}
        />
      </SlideOutHeight>
    </div>
  );
};

export default ClientOverviewHeaderItem;
